/**
 * The facts the help may quote, and the template syntax that quotes them.
 *
 * Help texts never spell out a button label, a status name, a limit or a
 * deletion period. They write `{{tx.create.submitMember}}` or
 * `{{limits.maxPhotosPerDamage}}`, and the placeholder is filled from the same
 * constant the screen or the API uses. A renamed button or a changed limit
 * therefore reaches the help without anybody editing it — and because the
 * help may now read wrong around the new word, help.lock.json records the
 * value every topic was last reviewed against and help.test.ts names each
 * topic whose facts moved ("Hilfe-Thema … prüfen").
 *
 * Keys:
 *   de.…, tx.…, mt.…   every string in the three text catalogues
 *                      (@fsv/core de, extra.ts, native.ts); functions are not
 *                      facts — a sentence with a slot is quoted via a limit,
 *                      except the few listed by their fixed part below
 *   status.<s>, category.<c>, view.<v>, role.<r>   the domain labels
 *   notice.<level>     the levels of a notice of the technicians
 *   limits.…           numbers from @fsv/core (validation.ts, limits.ts, zoom)
 *   retention.…        RETENTION, the deletion periods
 *   settings.…         the defaults a new club starts with
 *   tour.aircraft      the demo aircraft the tour walks through
 *
 * Pure and platform-free: web and app resolve the same text the same way.
 */
import {
  categoryLabel,
  noticeLevelLabel,
  COMMENT_MAX,
  de,
  DEFAULT_DUPLICATE_RADIUS,
  DEFAULT_MEMBER_PARTICIPATION,
  DEFAULT_REVIEW_DUE_DAYS,
  DESCRIPTION_MAX,
  LOGIN_CODE_LENGTH,
  LOGIN_CODE_MAX_ATTEMPTS,
  LOGIN_CODE_RESEND_SECONDS,
  LOGIN_CODE_SEND_WINDOW_MINUTES,
  LOGIN_CODE_SENDS_PER_DAY,
  LOGIN_CODE_SENDS_PER_WINDOW,
  LOGIN_CODE_TTL_MINUTES,
  MAX_IMAGE_BYTES,
  MAX_IMAGES_PER_DAMAGE,
  VIDEO_MAX_PER_DAMAGE,
  VIDEO_MAX_SECONDS,
  OFFLINE_CACHE_DAYS,
  OUTBOX_FIRST_RETRY_SECONDS,
  OUTBOX_MAX_RETRY_MINUTES,
  INVITATIONS_PER_HOUR,
  MAX_ADDITIONAL_EMAILS,
  PRICING,
  RETENTION,
  SIGNUP_CODE_TTL_MINUTES,
  SIGNUP_PENDING_HOURS,
  REVIEW_NOTE_MIN_LENGTH,
  roleLabel,
  SESSION_DAYS,
  statusLabel,
  THUMBNAIL_EDGE_PX,
  TITLE_MAX,
  TITLE_MIN,
  UNDO_WINDOW_SECONDS,
  UPLOAD_MAX_EDGE_PX,
  viewLabel,
} from '@fsv/core';
import { mt } from '../i18n/native';
import { tx } from '../i18n/extra';
import { MAX_SCALE } from '../lib/zoom';
import { TOUR_AIRCRAFT } from '../tour/tours';

export type FactValue = string | number;

function flatten(prefix: string, value: unknown, out: Map<string, FactValue>): void {
  if (typeof value === 'string' || typeof value === 'number') {
    out.set(prefix, value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((v, i) => flatten(`${prefix}.${i}`, v, out));
    return;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) flatten(`${prefix}.${k}`, v, out);
  }
  // functions (texts with a slot) are not facts
}

/** Numbers that are not in a catalogue. Named for what they mean, not where they live. */
const NUMBERS: Record<string, number> = {
  maxPhotosPerDamage: MAX_IMAGES_PER_DAMAGE,
  videoMaxSeconds: VIDEO_MAX_SECONDS,
  videoMaxPerDamage: VIDEO_MAX_PER_DAMAGE,
  maxPhotoMegabytes: MAX_IMAGE_BYTES / (1024 * 1024),
  photoEdgePx: UPLOAD_MAX_EDGE_PX,
  thumbnailEdgePx: THUMBNAIL_EDGE_PX,
  titleMin: TITLE_MIN,
  titleMax: TITLE_MAX,
  descriptionMax: DESCRIPTION_MAX,
  commentMax: COMMENT_MAX,
  reasonMin: REVIEW_NOTE_MIN_LENGTH,
  maxZoom: MAX_SCALE,
  loginCodeLength: LOGIN_CODE_LENGTH,
  loginCodeMinutes: LOGIN_CODE_TTL_MINUTES,
  loginCodeAttempts: LOGIN_CODE_MAX_ATTEMPTS,
  loginCodesPerWindow: LOGIN_CODE_SENDS_PER_WINDOW,
  loginCodeWindowMinutes: LOGIN_CODE_SEND_WINDOW_MINUTES,
  loginCodesPerDay: LOGIN_CODE_SENDS_PER_DAY,
  loginCodeResendSeconds: LOGIN_CODE_RESEND_SECONDS,
  sessionDays: SESSION_DAYS,
  undoSeconds: UNDO_WINDOW_SECONDS,
  offlineCacheDays: OFFLINE_CACHE_DAYS,
  outboxFirstRetrySeconds: OUTBOX_FIRST_RETRY_SECONDS,
  outboxMaxRetryMinutes: OUTBOX_MAX_RETRY_MINUTES,
  invitationsPerHour: INVITATIONS_PER_HOUR,
  maxAdditionalEmails: MAX_ADDITIONAL_EMAILS,
  signupCodeMinutes: SIGNUP_CODE_TTL_MINUTES,
  signupPendingHours: SIGNUP_PENDING_HOURS,
  founderPlaces: PRICING.founderClubs,
  founderDiscountPercent: PRICING.founderDiscountPercent,
};

/** What a new club starts with (@fsv/core limits.ts; API, seed and demo use the same). */
const SETTINGS_DEFAULTS = {
  reviewDueDays: DEFAULT_REVIEW_DUE_DAYS,
  /** Percent of the drawing's width. */
  duplicateRadiusPercent: Math.round(DEFAULT_DUPLICATE_RADIUS * 1000) / 10,
};

let cache: ReadonlyMap<string, FactValue> | null = null;

/** Every fact by key. Built once; the catalogues are constants. */
export function helpFacts(): ReadonlyMap<string, FactValue> {
  if (cache) return cache;
  const out = new Map<string, FactValue>();
  flatten('de', de, out);
  flatten('tx', tx, out);
  flatten('mt', mt, out);
  // Texts with a slot that the help names by their fixed part.
  out.set('tx.app.offline', tx.app.offline(null));
  flatten('status', statusLabel, out);
  flatten('category', categoryLabel, out);
  flatten('view', viewLabel, out);
  flatten('role', roleLabel, out);
  flatten('notice', noticeLevelLabel, out);
  flatten('limits', NUMBERS, out);
  flatten('retention', RETENTION, out);
  flatten('tour', { aircraft: TOUR_AIRCRAFT }, out);
  flatten('settings', { ...SETTINGS_DEFAULTS, participation: de.participation.level[DEFAULT_MEMBER_PARTICIPATION] }, out);
  cache = out;
  return out;
}

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;

/** The fact keys a template quotes, in order of appearance (duplicates kept once). */
export function factKeysOf(template: string): string[] {
  return [...new Set([...template.matchAll(PLACEHOLDER)].map((m) => m[1] as string))];
}

/** German number format: 8.5 → "8,5". */
function formatFact(value: FactValue): string {
  return typeof value === 'number' ? value.toLocaleString('de-DE') : value;
}

/**
 * Fills the placeholders. An unknown key renders as "…" rather than as the
 * raw key — help.test.ts makes an unknown key a failing test, so this is
 * only what a reader would see if that test were skipped.
 */
export function resolveHelpText(template: string): string {
  const facts = helpFacts();
  return template.replace(PLACEHOLDER, (_, key: string) => {
    const value = facts.get(key);
    return value === undefined ? '…' : formatFact(value);
  });
}
